import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AppUrlConstants } from '../../../core/constants/AppUrlConstants';

interface ChatLog {
  chatLogId: number;
  sessionId?: string;
  question: string;
  answer: string;
  similarityScore?: number | null;
  createdAt: string;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

@Component({
  selector: 'app-failed-queries',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './failed-queries.component.html',
  styleUrl: './failed-queries.component.css'
})
export class FailedQueriesComponent implements OnInit {

  data: ChatLog[] = [];
  loading = true;
  search = '';
  page = 1;
  rowsPerPage = 10;
  toasts: Toast[] = [];
  showDeleteModal = false;
  showEditModal = false;
  deleteTargetId: number | null = null;
  skeletonRows = Array.from({ length: 8 });

  // Edit modal fields
  editTargetRow: ChatLog | null = null;
  editQuestion = '';
  editAnswer = '';

  private toastIdCounter = 0;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.fetchData();
  }

  // ─── Computed ───
  get filtered(): ChatLog[] {
    return this.data.filter(item => {
      const q = (item.question || '').toLowerCase();
      const a = (item.answer || '').toLowerCase();
      const s = this.search.toLowerCase();
      return !this.search || q.includes(s) || a.includes(s);
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filtered.length / this.rowsPerPage));
  }

  get pageData(): ChatLog[] {
    return this.filtered.slice((this.page - 1) * this.rowsPerPage, this.page * this.rowsPerPage);
  }

  // ─── Fetch ───
  fetchData(): void {
    this.loading = true;
    this.http.get<any>(AppUrlConstants.GET_FAILED_QUERIES).subscribe({
      next: (json) => {
        const list = Array.isArray(json) ? json : json.data || json.results || [];
        this.data = list;
        this.loading = false;
      },
      error: () => {
        this.addToast('Failed to load data', 'error');
        this.data = [];
        this.loading = false;
      }
    });
  }

  // ─── Edit ───
  openEditModal(row: ChatLog): void {
    this.editTargetRow = row;
    this.editQuestion = row.question;
    this.editAnswer = row.answer;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editTargetRow = null;
  }

  handleEdit(): void {
    if (!this.editTargetRow) return;
    const q = this.editQuestion.trim();
    const a = this.editAnswer.trim();
    if (!q || !a) {
      this.addToast('Please fill in both question and answer', 'error');
      return;
    }

    const payload = { chatLogId: this.editTargetRow.chatLogId, question: q, answer: a };
    this.http.post<any>(AppUrlConstants.UPDATE_FAILED_QUERIES, payload).subscribe({
      next: () => {
        this.showEditModal = false;
        this.editTargetRow = null;
        this.addToast('Updated successfully');
        this.fetchData();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to update';
        this.addToast(msg, 'error');
      }
    });
  }

  // ─── Delete ───
  confirmDeleteRow(id: number): void {
    this.deleteTargetId = id;
    this.showDeleteModal = true;
  }

  handleDelete(): void {
    this.showDeleteModal = false;
    if (this.deleteTargetId === null) return;

    const id = this.deleteTargetId;
    this.deleteTargetId = null;

    this.http.delete<any>(`${AppUrlConstants.DELETE_FAILED_QUERIES}/${id}`).subscribe({
      next: () => {
        this.addToast('Row deleted');
        this.fetchData();
      },
      error: () => {
        this.addToast('Failed to delete', 'error');
      }
    });
  }

  // ─── Toast ───
  addToast(message: string, type: 'success' | 'error' = 'success'): void {
    const id = ++this.toastIdCounter;
    this.toasts.push({ id, message, type });
    setTimeout(() => this.removeToast(id), 3000);
  }

  removeToast(id: number): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  // ─── Filters ───
  onSearchChange(): void { this.page = 1; }

  // ─── Helpers ───
  formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  getRowClass(row: ChatLog, idx: number): string {
    const answer = row.answer || '';
    if (answer.trim().startsWith('Suggestion:')) return 'row-amber';
    if (answer.trim()) return 'row-red';
    return idx % 2 === 0 ? 'row-even' : 'row-odd';
  }

  getRowIndex(idx: number): number {
    return (this.page - 1) * this.rowsPerPage + idx + 1;
  }
}
